import { useState } from "react";
import { ExternalLink, Lock } from "lucide-react";
import { formatMoney } from "@/components/clinic-health-calculator/ClinicHealthCalculator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const SOURCE_LABELS = {
  manual: "Manual",
  recurring: "Recurring",
  lab_cost: "Lab",
  commission_payout: "Payout",
  migrated_receipt: "Imported",
  migrated_overhead: "Imported",
};

function sourceLabel(source) {
  return SOURCE_LABELS[source] || "Other";
}

function formatDate(value) {
  if (!value) return "—";
  // expense_date arrives as 'YYYY-MM-DD'. Render with no timezone surprises.
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return String(value);
  return `${month}/${day}/${year}`;
}

export default function ExpenseList({ entries, restrictedCount, canViewSensitive, currency, onVoid }) {
  const [voidTarget, setVoidTarget] = useState(null); // entry being voided
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const rows = Array.isArray(entries) ? entries : [];
  const restricted = Number(restrictedCount || 0);
  const showRestrictedFooter = restricted > 0 && !canViewSensitive;

  const openVoid = (entry) => {
    setVoidTarget(entry);
    setReason("");
  };

  const closeVoid = () => {
    if (submitting) return;
    setVoidTarget(null);
    setReason("");
  };

  const confirmVoid = async () => {
    const trimmed = reason.trim();
    if (!voidTarget || !trimmed) return;
    setSubmitting(true);
    try {
      await onVoid(voidTarget.id, trimmed);
      setVoidTarget(null);
      setReason("");
    } finally {
      setSubmitting(false);
    }
  };

  if (rows.length === 0) {
    return (
      <div
        data-testid="expenses-list"
        className="rounded-lg border border-slate-200 bg-white p-8 text-center text-[13px] text-slate-500"
      >
        No expenses recorded for this period.
      </div>
    );
  }

  return (
    <div data-testid="expenses-list" className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Vendor / Note</th>
              <th className="px-4 py-3">Branch</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => {
              const isVoided = entry.status === "voided";
              const category = entry.category || {};
              const vendorOrNote = entry.vendor_name || entry.description || "—";
              const rowTextClass = isVoided ? "text-slate-400 line-through" : "text-slate-800";
              return (
                <tr key={entry.id} className="border-b border-slate-100 last:border-0">
                  <td className={`whitespace-nowrap px-4 py-3 tabular-nums ${rowTextClass}`}>
                    {formatDate(entry.expense_date)}
                  </td>
                  <td className={`px-4 py-3 ${rowTextClass}`}>
                    <span className="inline-flex items-center gap-1.5">
                      {category.is_sensitive ? (
                        <Lock className="h-3.5 w-3.5 text-slate-400" strokeWidth={2.25} aria-label="Sensitive category" />
                      ) : null}
                      <span>{category.label || "—"}</span>
                    </span>
                  </td>
                  <td
                    className={`max-w-[260px] truncate px-4 py-3 ${rowTextClass}`}
                    title={isVoided && entry.void_reason ? `Voided: ${entry.void_reason}` : vendorOrNote}
                  >
                    {vendorOrNote}
                  </td>
                  <td className={`whitespace-nowrap px-4 py-3 ${rowTextClass}`}>
                    {entry.branch_name || "Clinic-wide"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                      {sourceLabel(entry.source)}
                    </span>
                  </td>
                  <td className={`whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums ${rowTextClass}`}>
                    {formatMoney(entry.amount_centavos, currency)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {entry.receipt_image_url ? (
                        <a
                          href={entry.receipt_image_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-slate-400 transition hover:text-teal-600"
                          aria-label="View receipt"
                          title="View receipt"
                        >
                          <ExternalLink className="h-4 w-4" strokeWidth={2.25} />
                        </a>
                      ) : null}
                      {entry.status === "recorded" ? (
                        <button
                          type="button"
                          onClick={() => openVoid(entry)}
                          className="text-[12px] font-semibold text-rose-600 transition hover:text-rose-700"
                        >
                          Void
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showRestrictedFooter ? (
        <div className="border-t border-slate-100 px-4 py-3 text-[12px] font-medium text-slate-500">
          {restricted} restricted line{restricted === 1 ? "" : "s"} {restricted === 1 ? "is" : "are"} included in the totals above but hidden here.
        </div>
      ) : null}

      <Dialog open={!!voidTarget} onOpenChange={(next) => { if (!next) closeVoid(); }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Void this expense</DialogTitle>
            <DialogDescription>
              Voiding removes this line from your totals. This cannot be undone — enter a reason for the record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label htmlFor="void-reason" className="text-[12px] font-semibold text-slate-600">
              Reason
            </label>
            <Textarea
              id="void-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. Entered twice, wrong amount, refunded"
              rows={3}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeVoid} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmVoid}
              disabled={submitting || !reason.trim()}
            >
              {submitting ? "Voiding…" : "Void expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
